// djin-cloud/web/src/components/charts/echarts/AreaProfileChart.tsx

import ReactECharts from 'echarts-for-react';
import { use as echartsUse, graphic } from 'echarts/core';
import type { EChartsCoreOption } from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, DataZoomComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { DefaultLabelFormatterCallbackParams } from 'echarts';
import { GAMMA_COLORS } from '../../../theme/gamma-echarts-theme';

echartsUse([LineChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, CanvasRenderer]);

export interface AreaProfileDataPoint {
  timestamp: string;
  consumption: number;
  generation: number;
}

interface AreaProfileChartProps {
  data: AreaProfileDataPoint[];
  height?: number;
}

export function AreaProfileChart({ data, height = 360 }: AreaProfileChartProps) {
  const option: EChartsCoreOption = {
    animation: true,
    grid: { left: 64, right: 24, top: 48, bottom: 70 },
    legend: {
      top: 8,
      data: ['Consumption', 'Generation'],
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: (params: DefaultLabelFormatterCallbackParams | DefaultLabelFormatterCallbackParams[]) => {
        if (!Array.isArray(params) || params.length === 0) return '';
        const first = params[0] as DefaultLabelFormatterCallbackParams & { axisValue?: string };
        const ts = first.axisValue
          ? new Date(first.axisValue).toLocaleString()
          : '';
        const consumption = params.find((p: DefaultLabelFormatterCallbackParams) => p.seriesName === 'Consumption');
        const generation = params.find((p: DefaultLabelFormatterCallbackParams) => p.seriesName === 'Generation');
        const consVal = Number((consumption?.value as unknown[])?.[1] ?? 0);
        const genVal = Number((generation?.value as unknown[])?.[1] ?? 0);
        const delta = genVal - consVal;
        const deltaColor = delta >= 0 ? GAMMA_COLORS.green : GAMMA_COLORS.red;
        const deltaSign = delta >= 0 ? '+' : '';

        const makeMarker = (color: string) =>
          `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:8px;height:8px;background-color:${color};"></span>`;

        return `
          <div style="font-size:12px;min-width:180px">
            <div style="margin-bottom:6px;color:${GAMMA_COLORS.textSecondary}">${ts}</div>
            <div>${makeMarker(GAMMA_COLORS.red)}<span style="font-family:monospace">Consumption: ${consVal.toFixed(2)} kWh</span></div>
            <div>${makeMarker(GAMMA_COLORS.green)}<span style="font-family:monospace">Generation: ${genVal.toFixed(2)} kWh</span></div>
            <div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.1)">
              <span style="color:${deltaColor};font-family:monospace;font-weight:600">Delta: ${deltaSign}${delta.toFixed(2)} kWh</span>
            </div>
          </div>
        `;
      },
    },
    xAxis: {
      type: 'time',
      axisLabel: {
        formatter: (value: number) => {
          const d = new Date(value);
          return `${d.getMonth() + 1}/${d.getDate()}`;
        },
      },
    },
    yAxis: {
      type: 'value',
      name: 'kWh',
      nameTextStyle: { color: GAMMA_COLORS.textMuted, fontSize: 11 },
    },
    dataZoom: [
      { type: 'inside', xAxisIndex: 0 },
      {
        type: 'slider',
        xAxisIndex: 0,
        bottom: 8,
        height: 24,
        borderColor: GAMMA_COLORS.border,
        backgroundColor: '#0d1520',
        dataBackground: {
          lineStyle: { color: GAMMA_COLORS.green, opacity: 0.3 },
          areaStyle: { color: GAMMA_COLORS.green, opacity: 0.1 },
        },
        selectedDataBackground: {
          lineStyle: { color: GAMMA_COLORS.green, opacity: 0.6 },
          areaStyle: { color: GAMMA_COLORS.green, opacity: 0.2 },
        },
        fillerColor: 'rgba(52, 211, 153, 0.08)',
        handleStyle: { color: GAMMA_COLORS.green },
        textStyle: { color: GAMMA_COLORS.textMuted, fontSize: 10 },
      },
    ],
    series: [
      {
        name: 'Consumption',
        type: 'line' as const,
        smooth: true,
        symbol: 'none',
        lineStyle: { width: 2, color: GAMMA_COLORS.red },
        areaStyle: {
          color: new graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: GAMMA_COLORS.red + '4d' },
            { offset: 1, color: GAMMA_COLORS.red + '00' },
          ]),
        },
        data: data.map((d) => [d.timestamp, d.consumption]),
      },
      {
        name: 'Generation',
        type: 'line' as const,
        smooth: true,
        symbol: 'none',
        lineStyle: { width: 2, color: GAMMA_COLORS.green },
        areaStyle: {
          color: new graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: GAMMA_COLORS.green + '4d' },
            { offset: 1, color: GAMMA_COLORS.green + '00' },
          ]),
        },
        data: data.map((d) => [d.timestamp, d.generation]),
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height }}
      theme="gamma"
      notMerge={false}
      lazyUpdate={true}
    />
  );
}
